
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { request_access_token, request_token_refresh, request_token_revoke } from '../services/authservice.js';
import { AuthContext } from '../AuthContext.jsx';
import { jwtDecode } from "jwt-decode"; 
import { useLocalStorage } from '../custom_hooks.js';

const AuthCtxtProvider = ({ children }) => {
    
    const navigate = useNavigate();
    const location = useLocation();

    const [authToken, setAuthToken] = useLocalStorage('hbns_auth_token', '');
    const [refreshToken, setRefreshToken] = useLocalStorage('hbns_refresh_token', null);

    const [claims, setClaims] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState(null);

    const replaceAuthData = (new_access_token, new_refresh_token) => {
        console.log('called replaceAuthData' + new_access_token + new_refresh_token)
        setAuthToken(new_access_token)
        const new_claims = jwtDecode(new_access_token)
        setClaims(new_claims)
        setRefreshToken(new_refresh_token)
    }

    const wipeAuthSession = () => {
        setAuthToken("");
        setClaims({})
        setRefreshToken("");
    }

    const refreshTokens = async () => {
        setIsLoading(true)

        const onRefreshSuccess = (tokens) => {
            console.log('called onRefreshSuccess')
            replaceAuthData(tokens.access_token, tokens.refresh_token)
            setAuthError('')
            setIsLoading(false)
        }

        const onRefreshFailure = (err) => {
            console.log('called onRefreshSuccess')
            if (err.response?.status > 300) {
                setAuthError(`token refresh error:  auth server responded with status ${err.response.status}`)
            } else {
                setAuthError(`token refresh error: exception ${err.toString()}`)
            }
            wipeAuthSession()
            setIsLoading(false)
        }

        await request_token_refresh(refreshToken, onRefreshSuccess, onRefreshFailure)
    }



    const validateSession = async () => {
        setIsLoading(true)
        const decoded_claims = jwtDecode(authToken)
        const exp = new Date(decoded_claims.exp * 1000);
        console.log('token expires at ' + exp.toLocaleString())
        const now = Date.now();
        if (now > exp) {
            console.log('token is expired, attempting refresh')
            await refreshTokens()
        } else {
            setClaims(decoded_claims)
            setIsLoading(false)
        }
    }

    const handleLogin = async (username, password) => {

        setIsLoading(true)

        const onLoginSuccess = (tokens) => {
            replaceAuthData(tokens.access_token, tokens.refresh_token)
            setAuthError(null)
            setIsLoading(false)
            // If the location state is null when the user is at the '/login' path, that means '/login' was the first path they visited, so by default we navigate to the '/editor' path - otherwise, they must have been navigated here by the ProtectedRoute component from another location, so get get the last saved location and navigate back to there again.
            console.log('in onLoginSuccess, location state is ' + JSON.stringify(location.state))
            const origin = location.state?.from?.pathname || '/editor';
            navigate(origin);
        };

        const onLoginFailure = (err) => {
            setIsLoading(false)
            setAuthError(err)
        }
        await request_access_token(username, password, onLoginSuccess, onLoginFailure);
    }

    const handleLogout = async () => {
        const onRevokeFailure = (err) => {
            console.error('an error occured during logout;  the request to the revoke token api failed for token = ' + refreshToken + 'err = ' + err.toString())
        }

        request_token_revoke(refreshToken, () => { }, onRevokeFailure)
        wipeAuthSession()
    };


    const value = {
        authToken,
        refreshToken,
        claims,
        isLoading,
        authError,
        onLogin: handleLogin,
        onLogout: handleLogout
    };


    useEffect(() => {
        if (authToken) {
            validateSession()
        } else {
            setIsLoading(false)
        }
    }, [])



    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthCtxtProvider;